import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ssoApi, getTokenKey } from "./sso-api";
import { setCookie } from "@/hooks/cookie-helper";

interface SsoAuthSliceState {
  isAuthenticated: boolean;
  sessionId: string | null;
  user: SSOUser | null;
  app: SSOApp | null;
  error: string | null;
  isMfaRequired: boolean;
}

const initialState: SsoAuthSliceState = {
  user: null,
  sessionId: null,
  isAuthenticated: false,
  error: null,
  isMfaRequired: false,
  app: null,
};

const SsoAuthSlice = createSlice({
  name: "ssoAuthSlice",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<SSOUser>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.user = null;
      state.sessionId = null;
      state.isAuthenticated = false;
    },
    setAppConfig: (state, action: PayloadAction<SSOApp>) => {
      state.app = action.payload;
    },
    setMfaRequired: (state, action: PayloadAction<boolean>) => {
      state.isMfaRequired = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      ssoApi.endpoints.login.matchFulfilled,
      (state, { payload }: { payload: SSOLoginResponse }) => {
        state.isAuthenticated = payload.is_authenticated ?? false;
        state.sessionId = payload.sessionId || null;
        state.user = payload.user || null;
        state.error = null;
        setCookie({ name: getTokenKey(), value: payload.token ?? "", days: 1 });
      }
    );
    builder.addMatcher(
      ssoApi.endpoints.completeMfa.matchFulfilled,
      (state, { payload }: { payload: SSOCompleteMfaResponse }) => {
        state.isAuthenticated = payload.is_authenticated;
        state.sessionId = payload.sessionId;
        state.user = payload.user;
        state.error = null;
      }
    );
    builder.addMatcher(
      ssoApi.endpoints.changePassword.matchFulfilled,
      (state, { payload }: { payload: SSOChangePasswordResponse }) => {
        state.error = payload.success ? null : payload.message;
      }
    );
    builder.addMatcher(
      ssoApi.endpoints.retrieveAppConfig.matchFulfilled,
      (state, { payload }: { payload: SSOApp }) => {
        state.app = payload;
      }
    );
    builder.addMatcher(
      ssoApi.endpoints.editUser.matchFulfilled,
      (state, { payload }: { payload: SSOEditUserResponse }) => {
        state.user = payload;
        state.error = null;
      }
    );
  },
});

export const ssoAuthActions = SsoAuthSlice.actions;
export default SsoAuthSlice.reducer;
